import { useForm, revalidateLogic } from "@tanstack/react-form"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { CalendarPlus } from "lucide-react"
import { useState } from "react"

import {
  EventFormFields,
  projectEventInput,
  validateEventInput,
  type EventFormState,
} from "@/components/guild-events-admin/event-form-fields"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { SectionKicker } from "@/components/shared/section-kicker"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { toast } from "@/components/ui/toast"
import { guildEventsApi } from "@/lib/guild-events-api"
import { guildEventsKeys } from "@/lib/query-keys"

export const Route = createFileRoute("/_authed/_admin/guild-events/new")({
  component: GuildEventsCreateRoute,
})
type CreateEventFormState = EventFormState & { channelId: string }

const defaultValues: CreateEventFormState = {
  eventKind: "raid",
  title: "",
  description: "",
  eventRoles: ["tank", "dps", "healer"],
  startAt: "",
  signupCloseAt: "",
  rsvpEnabled: true,
  channelId: "",
}

function GuildEventsCreateRoute() {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: guildEventsApi.createEvent,
    onSuccess: async (event) => {
      await queryClient.invalidateQueries({
        queryKey: guildEventsKeys.adminList(),
      })
      await navigate({
        to: "/guild-events/$eventId",
        params: { eventId: event.id },
      })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const form = useForm({
    defaultValues,
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: {
      onDynamic: ({ value }) =>
        validateEventInput(value, { requireFutureClose: true }),
    },
    onSubmitInvalid: () => {
      setFormError("Fix the highlighted errors.")
    },
    onSubmit: ({ value }) => {
      setFormError(null)
      createMutation.mutate({
        ...projectEventInput(value),
        channelId: value.channelId.trim() || undefined,
      })
    },
  })

  return (
    <div className="grid gap-4 md:gap-5">
      <Card className="page-intro border-primary/10">
        <PageIntroSplitHeader
          kicker={
            <SectionKicker icon={<CalendarPlus />}>Guild Events</SectionKicker>
          }
          title="Create a new guild event"
          description="Create raid events with role signups or attendance events with a simple RSVP. Announcements and lifecycle jobs are scheduled automatically."
        />
      </Card>

      <Card>
        <CardContent className="pt-5">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void form.handleSubmit()
            }}
            noValidate
            className="grid gap-4 md:grid-cols-2"
          >
            <form.Subscribe selector={(state) => state.values}>
              {(values) => (
                <EventFormFields
                  bindings={{
                    values,
                    eventKind: (children) => (
                      <form.Field name="eventKind">{children}</form.Field>
                    ),
                    title: (children) => (
                      <form.Field name="title">{children}</form.Field>
                    ),
                    description: (children) => (
                      <form.Field name="description">{children}</form.Field>
                    ),
                    eventRoles: (children) => (
                      <form.Field name="eventRoles">{children}</form.Field>
                    ),
                    startAt: (children) => (
                      <form.Field name="startAt">{children}</form.Field>
                    ),
                    signupCloseAt: (children) => (
                      <form.Field name="signupCloseAt">{children}</form.Field>
                    ),
                    rsvpEnabled: (children) => (
                      <form.Field name="rsvpEnabled">{children}</form.Field>
                    ),
                    setEventRoles: (eventRoles) =>
                      form.setFieldValue("eventRoles", eventRoles),
                    setSignupCloseAt: (signupCloseAt) =>
                      form.setFieldValue("signupCloseAt", signupCloseAt ?? ""),
                  }}
                />
              )}
            </form.Subscribe>
            <form.Field name="channelId">
              {(field) => (
                <div className="grid gap-2">
                  <label
                    htmlFor="event-channel-id"
                    className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
                  >
                    Channel ID (optional)
                  </label>
                  <Input
                    id="event-channel-id"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    placeholder="Defaults to the configured events channel"
                  />
                </div>
              )}
            </form.Field>

            {formError ? (
              <div
                role="alert"
                className="text-sm text-destructive md:col-span-2"
              >
                {formError}
              </div>
            ) : null}

            <div className="flex gap-2 md:col-span-2">
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create Event"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => void navigate({ to: "/guild-events" })}
              >
                Back to Events
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
